<script>
  import { link, navigate } from "svelte-routing";
  import { writable } from 'svelte/store';
  import pb from '../../lib/pocketbase'; // Importación del cliente PocketBase
  import { onMount, onDestroy } from 'svelte';
  import { t } from 'svelte-i18n';

  const google = "../assets/img/google.svg";
  const companyName = "NONOSKY";
  const companyLogo = "/assets/images/logo_nonosky.svg"; // Path to company logo
  
  let email = '';
  let password = '';
  let rememberMe = false;
  const error = writable(null);

  onMount(() => {
    // Set Nonosky brand colors - black background with red accents
    document.body.style.background = '#000000';
    document.body.style.backgroundSize = 'cover';
    document.body.style.backgroundPosition = 'center center';
    document.body.style.backgroundAttachment = 'fixed';
    document.body.style.backgroundRepeat = 'no-repeat';
  });

  onDestroy(() => {
    document.body.style.background = '';
    document.body.style.backgroundSize = '';
    document.body.style.backgroundPosition = '';
    document.body.style.backgroundAttachment = '';
    document.body.style.backgroundRepeat = '';
  });

  // Función para iniciar sesión con email y contraseña
  async function handleSubmit(event) {
    event.preventDefault();
    
    if (!email || !password) {
      error.set($t('login.requiredFields'));
      return;
    }

    try {
      const authData = await pb.collection('users').authWithPassword(email, password);

      // Guardar token y usuario autenticado
      localStorage.setItem('pb_token', pb.authStore.token);
      localStorage.setItem('pb_user', JSON.stringify(pb.authStore.model));
      
      // Set token expiration based on remember me
      if (rememberMe) {
        // Set longer expiration for remember me (30 days)
        pb.authStore.save(pb.authStore.token, pb.authStore.model, { expires: 30 * 24 * 60 * 60 });
      } else {
        // Set shorter expiration for normal login (1 day)
        pb.authStore.save(pb.authStore.token, pb.authStore.model, { expires: 24 * 60 * 60 });
      }

      navigate('/admin/dashboard');  // Redirige al dashboard
    } catch (err) {
      error.set(err.message || 'Error al iniciar sesión');
    }
  }

  // Función para iniciar sesión con Google OAuth
  async function handleGoogleLogin() {
    try {
      const authData = await pb.collection('users').authWithOAuth2({ provider: 'google' });

      // Guardar token y usuario autenticado
      localStorage.setItem('pb_token', pb.authStore.token);
      localStorage.setItem('pb_user', JSON.stringify(pb.authStore.model));

      const userName = authData.meta.rawUser.name;

      // Actualizar el usuario en la colección si es necesario
      await pb.collection('users').update(pb.authStore.model.id, { name: userName });

      console.log("Nombre de usuario guardado:", userName);

      navigate('/admin/dashboard');  // Redirige al dashboard
    } catch (err) {
      error.set($t('login.googleError') + ': ' + (err.message || ''));
    }
  }
</script>

<div class="container mx-auto px-4 h-full">
  <div class="flex content-center items-center justify-center h-full">
    <div class="w-full lg:w-4/12 px-4">
      <div class="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-gray-900/95 border border-red-600">
        <!-- Logo Section -->
        <div class="flex justify-center items-center py-8 px-4">
          <div class="text-center">
            <!-- Logo Container with cool styling -->
            <div class="mb-4">
              <div class="inline-block p-4 bg-white rounded-full shadow-lg transform hover:scale-105 transition-transform duration-300">
                <img 
                  src={companyLogo} 
                  alt="{companyName}" 
                  class="h-16 w-auto object-contain"
                  on:error={(e) => {
                    // Fallback if logo doesn't exist - create a text-based logo
                    e.target.style.display = 'none';
                    e.target.nextElementSibling.style.display = 'block';
                  }}
                />
                <!-- Fallback text logo -->
                <div class="hidden text-center">
                  <div class="text-3xl font-bold text-red-600 mb-1">1S</div>
                  <div class="text-sm font-semibold text-gray-700">{companyName}</div>
                </div>
              </div>
            </div>
            <!-- Company Name and Tagline -->
            <div class="mb-2">
              <h1 class="text-2xl font-bold text-red-600 mb-1">{companyName}</h1>
              <p class="text-sm text-gray-300 font-medium">{$t('login.systemTagline')}</p>
            </div>
          </div>
        </div>

        <div class="flex-auto px-4 lg:px-10 py-10 pt-0">
          <div class="text-red-600 text-center mb-3 font-bold">
            <small>{$t('login.title')}</small>
          </div>
          <form on:submit={handleSubmit}>
            <div class="relative w-full mb-3">
              <label class="block uppercase text-white text-xs font-bold mb-2" for="grid-email">
                {$t('login.emailLabel')}
              </label>
              <input
                id="grid-email"
                type="email"
                bind:value={email}
                class="border-0 px-3 py-3 placeholder-gray-400 text-white bg-red-800 rounded text-sm shadow focus:outline-none focus:ring focus:ring-red-600 w-full ease-linear transition-all duration-150"
                placeholder="{$t('login.emailPlaceholder')}"
              />
            </div>

            <div class="relative w-full mb-3">
              <label class="block uppercase text-white text-xs font-bold mb-2" for="grid-password">
                {$t('login.passwordLabel')}
              </label>
              <input
                id="grid-password"
                type="password"
                bind:value={password}
                class="border-0 px-3 py-3 placeholder-gray-400 text-white bg-red-800 rounded text-sm shadow focus:outline-none focus:ring focus:ring-red-600 w-full ease-linear transition-all duration-150"
                placeholder="{$t('login.passwordPlaceholder')}"
              />
            </div>

            <div>
              <label class="inline-flex items-center cursor-pointer">
                <input
                  id="customCheckLogin"
                  type="checkbox"
                  bind:checked={rememberMe}
                  class="form-checkbox border-0 rounded text-red-600 ml-1 w-5 h-5 ease-linear transition-all duration-150"
                />
                <span class="ml-2 text-sm font-semibold text-white">
                  {$t('login.rememberMe')}
                </span>
              </label>
            </div>

            <div class="text-center mt-6">
              <button
                class="bg-red-800 text-white active:bg-red-900 text-sm font-bold uppercase px-6 py-3 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 w-full ease-linear transition-all duration-150"
                type="submit"
              >
                {$t('login.signIn')}
              </button>
            </div>

            {#if $error}
              <div class="text-red-500 text-sm mt-2">{ $error }</div>
            {/if}
          </form>
        </div>
      </div>
      
    </div>
  </div>
</div>
